// Growth Opportunities views — three dashboards grounded in Byron Sharp's
// "How Brands Grow": brands grow by expanding penetration, not deepening loyalty.
//
// All three views share the same brand + session-type filters and the same
// three API endpoints (growth/opportunities-by-age/gender/geo).  The endpoints
// return per-segment: category penetration, brand penetration, and the gap
// (opportunity_users / opportunity_pct).
//
// View 1 — Category Target Audience : who does the category reach?
// View 2 — Brand Target Audience    : which category users isn't the brand reaching?
// View 3 — Penetration Targets      : priority matrix across all dimensions
import { useState, useEffect, useMemo, useCallback, useContext } from 'react';
import ReactECharts from 'echarts-for-react';
import * as echarts from 'echarts';
import { ThemeContext } from '../context/ThemeContext';
import { useCategory } from '../context/CategoryContext';
import { useMonth } from '../context/MonthContext';
import { useApi, useMetaData } from '../hooks/useApi';
import { Growth, CategoryBehaviour } from '../utils/api';
import { BarChart, BubbleChart, ChartCard, ChartLoader, ChartError } from '../components/Charts';
import { COLORS, PALETTE, AGE_ORDER, fmtNum, fmtPct, fmtMonth } from '../utils/theme';
import { geoName } from '../utils/postcodes';
import { UK_MAP_NAME, POSTCODE_REGION, REGION_POSTCODES, loadUKMap } from '../utils/ukMap';

// ── Shared helpers ────────────────────────────────────────────────────────────

const GENDER_LABELS = { M: 'Male', F: 'Female' };
const DIM_COLORS    = { age: COLORS.teal, gender: COLORS.blue, geo: COLORS.indigo };
const DIM_LABELS    = { age: 'Age', gender: 'Gender', geo: 'Geography' };

function sortByAge(rows) {
  return [...rows].sort(
    (a, b) => AGE_ORDER.indexOf(a.segment) - AGE_ORDER.indexOf(b.segment),
  );
}

function sumField(rows, field) {
  return (rows || []).reduce((acc, r) => acc + (r[field] || 0), 0);
}

function topByField(rows, field) {
  if (!rows || !rows.length) return null;
  return rows.reduce((best, r) => (r[field] > (best?.[field] || 0) ? r : best), null);
}

// Signed bar series (positive=teal, negative=red)
function signedSeries(rows, field, label) {
  return [{
    name: label,
    data: rows.map(r => ({
      value: r[field],
      itemStyle: { color: r[field] >= 0 ? COLORS.positive : COLORS.negative },
    })),
  }];
}

// ── Opportunity choropleth map ────────────────────────────────────────────────
// geoRows: raw rows from opportunitiesByGeo (segment = raw postcode area code)
function OpportunityGeoMap({ geoRows = [], selApp, effectiveCat, selectedMonth }) {
  const { dark } = useContext(ThemeContext);
  const [mapReady, setMapReady] = useState(false);
  const [geoError, setGeoError] = useState(false);
  const [selectedRegion, setSelectedRegion] = useState('London');

  useEffect(() => { loadUKMap(ok => { setMapReady(ok); setGeoError(!ok); }); }, []);

  const valid = useMemo(
    () => geoRows.filter(r => r.segment && r.segment.toLowerCase() !== 'unknown'),
    [geoRows],
  );

  // Region totals for map coloring (opportunity_users sum per region)
  const { regionData, maxVal } = useMemo(() => {
    const totals = {};
    valid.forEach(r => {
      const region = POSTCODE_REGION[r.segment];
      if (region) totals[region] = (totals[region] || 0) + (r.opportunity_users || 0);
    });
    const regionData = Object.entries(totals).map(([name, value]) => ({ name, value }));
    return { regionData, maxVal: Math.max(1, ...regionData.map(d => d.value)) };
  }, [valid]);

  // Top 15 for the selected region, ordered by opportunity_users desc
  const top15 = useMemo(() => {
    const codes = new Set(REGION_POSTCODES[selectedRegion] || []);
    const inRegion = selectedRegion ? valid.filter(r => codes.has(r.segment)) : valid;
    return [...inRegion].sort((a, b) => b.opportunity_users - a.opportunity_users).slice(0, 15);
  }, [valid, selectedRegion]);

  const usersSeries = useMemo(() => [{
    name: 'Opportunity Users',
    color: COLORS.teal,
    data: top15.map(r => r.opportunity_users),
  }], [top15]);

  const gapSeries = useMemo(() => [{
    name: 'Opportunity Gap %',
    color: COLORS.indigo,
    data: top15.map(r => r.opportunity_pct),
  }], [top15]);

  const drillHeight = Math.max(300, Math.min(top15.length, 15) * 34 + 68);

  const mapOption = useMemo(() => {
    if (!mapReady) return null;
    return {
      backgroundColor: 'transparent',
      tooltip: {
        trigger: 'item',
        formatter: p => `<b>${p.name}</b><br/>Opportunity Users: <b>${fmtNum(p.value || 0)}</b>`,
        backgroundColor: dark ? '#1e2633' : '#fff',
        borderColor: dark ? '#2d3748' : '#E5E7EB',
        borderWidth: 1,
        textStyle: { color: dark ? '#F9FAFB' : '#111827', fontSize: 12 },
        extraCssText: 'box-shadow:0 4px 12px rgba(0,0,0,0.12);border-radius:8px;',
      },
      visualMap: {
        min: 0, max: maxVal,
        text: ['High', 'Low'],
        realtime: false, calculable: true,
        inRange: { color: ['#FEF3C7', '#F59E0B', '#D97706', '#92400E'] },
        textStyle: { color: dark ? '#9CA3AF' : '#6B7280', fontSize: 10 },
        right: 8, top: 'middle', itemWidth: 12, itemHeight: 100,
      },
      series: [{
        type: 'map',
        map: UK_MAP_NAME,
        roam: true,
        data: regionData,
        selectedMode: 'single',
        emphasis: {
          label: { show: true, fontSize: 10, color: '#fff' },
          itemStyle: { areaColor: '#1DB89A' },
        },
        select: {
          label: { show: true, fontSize: 10, color: '#fff' },
          itemStyle: { areaColor: '#1DB89A' },
        },
        label: { show: false },
        itemStyle: {
          areaColor: dark ? '#1e2633' : '#E8ECF4',
          borderColor: dark ? '#374151' : '#fff',
          borderWidth: 0.8,
        },
      }],
    };
  }, [mapReady, regionData, maxVal, dark]);

  const onMapClick = useCallback(e => {
    if (!e?.name) return;
    setSelectedRegion(prev => prev === e.name ? 'London' : e.name);
  }, []);

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, alignItems: 'stretch' }}>
      {/* Choropleth */}
      <ChartCard
        title="Opportunity Users by Region"
        sub={`${effectiveCat} · ${selApp} · ${fmtMonth(selectedMonth)} · amber = high opportunity · click region to drill in`}
      >
        {geoError && <ChartError message="Failed to load UK map" height={520} />}
        {!geoError && (!mapReady || !geoRows.length) && <ChartLoader height={520} />}
        {!geoError && mapReady && geoRows.length > 0 && mapOption && (
          <ReactECharts
            key={selectedRegion ?? 'full'}
            echarts={echarts}
            option={mapOption}
            style={{ height: 520 }}
            onEvents={{ click: onMapClick }}
            notMerge
          />
        )}
      </ChartCard>

      {/* Top 15 by opportunity users */}
      <ChartCard
        title={`Opportunity Users — ${selectedRegion ?? 'All Regions'}`}
        sub={`${effectiveCat} · ${selApp} · ${fmtMonth(selectedMonth)} · top 15 postcodes`}
      >
        {!geoRows.length
          ? <ChartLoader height={drillHeight} />
          : top15.length === 0
            ? <div className="empty-state" style={{ height: drillHeight }}>No data{selectedRegion ? ` for ${selectedRegion}` : ''}</div>
            : <BarChart
                categories={top15.map(r => geoName(r.segment))}
                series={usersSeries}
                horizontal
                xFormatter={v => fmtNum(v)}
                height={drillHeight}
              />
        }
      </ChartCard>

      {/* Top 15 by opportunity gap % (same postcode order) */}
      <ChartCard
        title={`Opportunity Gap % — ${selectedRegion ?? 'All Regions'}`}
        sub={`${effectiveCat} · ${selApp} · ${fmtMonth(selectedMonth)} · penetration headroom`}
      >
        {!geoRows.length
          ? <ChartLoader height={drillHeight} />
          : top15.length === 0
            ? <div className="empty-state" style={{ height: drillHeight }}>No data{selectedRegion ? ` for ${selectedRegion}` : ''}</div>
            : <BarChart
                categories={top15.map(r => geoName(r.segment))}
                series={gapSeries}
                horizontal
                xFormatter={v => `${v}%`}
                height={drillHeight}
              />
        }
      </ChartCard>
    </div>
  );
}

// ── Shared filter bar + brand picker ─────────────────────────────────────────

function useGrowthState() {
  const { category }   = useCategory();
  const { month }      = useMonth();
  const meta           = useMetaData();
  const [sessionType, setSessionType] = useState('open_app');
  const [app, setApp]                 = useState('');

  const effectiveCat  = category || 'ALL';
  const selectedMonth = month || meta.latestMonth;

  // Derive brand list from the brand-penetration-by-age endpoint (cached)
  const appsReq = useApi(
    CategoryBehaviour.brandPenetrationByAge,
    { category: effectiveCat, session_type: sessionType, month_id: selectedMonth },
    !!selectedMonth,
  );
  const apps   = useMemo(
    () => [...new Set((appsReq.data || []).map(r => r.application))],
    [appsReq.data],
  );
  const selApp = apps.includes(app) ? app : (apps[0] || '');

  const enabled = !!selectedMonth && !!selApp;
  const params  = { category: effectiveCat, session_type: sessionType, month_id: selectedMonth, application: selApp };

  return { meta, effectiveCat, selectedMonth, sessionType, setSessionType, apps, selApp, setApp, enabled, params };
}

function FilterBar({ meta, sessionType, setSessionType, apps, selApp, setApp }) {
  const { dark } = useContext(ThemeContext);
  return (
    <div>
      {/* Brand pill picker */}
      {apps.length > 0 && (
        <div style={{
          background: dark ? 'rgba(29,184,154,0.07)' : 'rgba(29,184,154,0.06)',
          border: `1px solid ${dark ? 'rgba(29,184,154,0.20)' : 'rgba(29,184,154,0.18)'}`,
          borderRadius: 12,
          padding: '14px 18px',
          marginBottom: 14,
        }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: COLORS.teal, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>
            Choose a brand to explore
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {apps.map((a, i) => {
              const active = a === selApp;
              return (
                <button
                  key={a}
                  onClick={() => setApp(a)}
                  style={{
                    padding: '6px 16px',
                    borderRadius: 100,
                    border: active ? `2px solid ${PALETTE[i % PALETTE.length]}` : `1.5px solid ${dark ? '#374151' : '#E5E7EB'}`,
                    background: active ? PALETTE[i % PALETTE.length] : 'transparent',
                    color: active ? '#fff' : (dark ? '#D1D5DB' : '#374151'),
                    fontWeight: active ? 700 : 500,
                    fontSize: 13,
                    cursor: 'pointer',
                    transition: 'all 0.15s',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {a}
                </button>
              );
            })}
          </div>
        </div>
      )}
      {/* Session filter */}
      <div className="filters-bar">
        <span className="filter-label">Session</span>
        <select className="filter-select" value={sessionType} onChange={e => setSessionType(e.target.value)}>
          {meta.sessionTypes.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>
    </div>
  );
}

// ── KPI tile ─────────────────────────────────────────────────────────────────

function KpiTile({ label, value, sub, color }) {
  return (
    <div className="kpi-card">
      <div className="kpi-label">{label}</div>
      <div className="kpi-value" style={color ? { color } : {}}>{value}</div>
      {sub && <div className="kpi-delta neutral">{sub}</div>}
    </div>
  );
}

// ── View 1: Category Target Audience ─────────────────────────────────────────

export function CategoryTargetAudienceView() {
  const state = useGrowthState();
  const { meta, effectiveCat, selectedMonth, sessionType, setSessionType, apps, selApp, setApp, enabled, params } = state;

  const ageReq    = useApi(Growth.opportunitiesByAge,    params, enabled);
  const genderReq = useApi(Growth.opportunitiesByGender, params, enabled);
  const geoReq    = useApi(Growth.opportunitiesByGeo,    { ...params, limit: 0 }, enabled);

  const ageRows    = useMemo(() => sortByAge(ageReq.data    || []), [ageReq.data]);
  const genderRows = useMemo(() => (genderReq.data || []).map(r => ({ ...r, segment: GENDER_LABELS[r.segment] || r.segment })), [genderReq.data]);
  const geoRows    = useMemo(() => (geoReq.data   || []).map(r => ({ ...r, segment: geoName(r.segment) })),       [geoReq.data]);

  const nat = ageRows[0] || null;

  const catPenGap = nat
    ? +(nat.national_cat_pct - nat.national_brand_pct).toFixed(2)
    : null;

  const oppUsersAge = sumField(ageRows, 'opportunity_users');

  const groupedSeries = rows => [
    { name: 'Category', color: COLORS.teal, data: rows.map(r => r.cat_penetration_pct) },
    { name: selApp,     color: COLORS.blue,  data: rows.map(r => r.brand_penetration_pct) },
  ];

  const loading = ageReq.loading || genderReq.loading;

  return (
    <div>
      <FilterBar meta={meta} sessionType={sessionType} setSessionType={setSessionType} apps={apps} selApp={selApp} setApp={setApp} />

      {nat && (
        <div className="kpi-grid" style={{ marginBottom: 16 }}>
          <KpiTile
            label="Category Users (age total)"
            value={fmtNum(nat.users_total_cat)}
            sub={`${nat.national_cat_pct}% of panel · ${fmtMonth(selectedMonth)}`}
          />
          <KpiTile
            label={`${selApp} Users (age total)`}
            value={fmtNum(nat.users_total_brand)}
            sub={`${nat.national_brand_pct}% of panel`}
          />
          <KpiTile
            label="Opportunity (age total)"
            value={fmtNum(oppUsersAge)}
            sub="Category users not yet on brand"
          />
          <KpiTile
            label="Penetration Gap — National"
            value={`${catPenGap >= 0 ? '+' : ''}${catPenGap} pp`}
            color={catPenGap >= 0 ? COLORS.positive : COLORS.negative}
            sub={`${nat.national_cat_pct}% cat vs ${nat.national_brand_pct}% brand`}
          />
        </div>
      )}

      {/* Age Band */}
      <div className="trends-section-label">By Age Band</div>
      {ageReq.loading && <ChartLoader />}
      {ageReq.error   && <ChartError message={ageReq.error} />}
      {!ageReq.loading && !ageReq.error && ageRows.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
          <ChartCard
            title="Age Band — Penetration %"
            sub={`${effectiveCat} · ${fmtMonth(selectedMonth)} · category vs ${selApp}`}
          >
            <BarChart
              categories={ageRows.map(r => r.segment)}
              series={groupedSeries(ageRows)}
              height={280}
              xFormatter={v => `${v}%`}
            />
          </ChartCard>
          <ChartCard
            title="Age Band — Penetration Gap (Category − Brand)"
            sub="Positive = category leads brand in this segment"
          >
            <BarChart
              categories={ageRows.map(r => r.segment)}
              series={signedSeries(ageRows, 'opportunity_pct', 'Gap %')}
              height={280}
              xFormatter={v => `${v} pp`}
            />
          </ChartCard>
        </div>
      )}

      {/* Gender */}
      <div className="trends-section-label">By Gender</div>
      {genderReq.loading && <ChartLoader height={160} />}
      {genderReq.error   && <ChartError message={genderReq.error} height={160} />}
      {!genderReq.loading && !genderReq.error && genderRows.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
          <ChartCard title="Gender — Penetration %" sub={`${effectiveCat} · ${fmtMonth(selectedMonth)}`}>
            <BarChart
              categories={genderRows.map(r => r.segment)}
              series={groupedSeries(genderRows)}
              height={200}
              xFormatter={v => `${v}%`}
            />
          </ChartCard>
          <ChartCard title="Gender — Penetration Gap">
            <BarChart
              categories={genderRows.map(r => r.segment)}
              series={signedSeries(genderRows, 'opportunity_pct', 'Gap %')}
              height={200}
              xFormatter={v => `${v} pp`}
            />
          </ChartCard>
        </div>
      )}

      {/* Geography — opportunity map */}
      <div className="trends-section-label">By Geography</div>
      <div style={{ marginBottom: 16 }}>
        <OpportunityGeoMap
          geoRows={geoReq.data || []}
          selApp={selApp}
          effectiveCat={effectiveCat}
          selectedMonth={selectedMonth}
        />
      </div>
    </div>
  );
}

// ── View 2: Brand Target Audience ─────────────────────────────────────────────

export function BrandTargetAudienceView() {
  const state = useGrowthState();
  const { meta, effectiveCat, selectedMonth, sessionType, setSessionType, apps, selApp, setApp, enabled, params } = state;

  const ageReq    = useApi(Growth.opportunitiesByAge,    params, enabled);
  const genderReq = useApi(Growth.opportunitiesByGender, params, enabled);
  const geoReq    = useApi(Growth.opportunitiesByGeo,    { ...params, limit: 0 }, enabled);

  const ageRows    = useMemo(() => sortByAge(ageReq.data    || []), [ageReq.data]);
  const genderRows = useMemo(() => (genderReq.data || []).map(r => ({ ...r, segment: GENDER_LABELS[r.segment] || r.segment })), [genderReq.data]);
  const geoRowsSorted = useMemo(
    () => [...(geoReq.data || [])].sort((a, b) => b.opportunity_users - a.opportunity_users),
    [geoReq.data],
  );

  const nat            = ageRows[0] || null;
  const oppUsersAge    = sumField(ageRows,    'opportunity_users');
  const oppUsersGender = sumField(genderRows, 'opportunity_users');
  const oppUsersGeo    = sumField(geoRowsSorted, 'opportunity_users');

  const topAgeOpp    = topByField(ageRows,       'opportunity_users');
  const topGeoOpp    = topByField(geoReq.data || [], 'opportunity_users');
  const brandShare   = nat && nat.users_total_cat
    ? fmtPct((nat.users_total_brand / nat.users_total_cat) * 100)
    : '—';

  const oppSeries    = rows => [{ name: 'Opportunity Users', color: COLORS.teal,   data: rows.map(r => r.opportunity_users) }];
  const oppPctSeries = rows => [{ name: 'Opportunity %',     color: COLORS.indigo, data: rows.map(r => r.opportunity_pct) }];

  return (
    <div>
      <FilterBar meta={meta} sessionType={sessionType} setSessionType={setSessionType} apps={apps} selApp={selApp} setApp={setApp} />

      {nat && (
        <div className="kpi-grid" style={{ marginBottom: 16 }}>
          <KpiTile
            label="Category Users (pool)"
            value={fmtNum(nat.users_total_cat)}
            sub={`${nat.national_cat_pct}% of panel · ${fmtMonth(selectedMonth)}`}
          />
          <KpiTile
            label={`${selApp} Market Share`}
            value={brandShare}
            sub="Brand users as % of category users"
          />
          <KpiTile
            label="Best Age Opportunity"
            value={topAgeOpp ? topAgeOpp.segment : '—'}
            sub={topAgeOpp ? `${fmtNum(topAgeOpp.opportunity_users)} users · ${topAgeOpp.opportunity_pct}% gap` : ''}
          />
          <KpiTile
            label="Best Geo Opportunity"
            value={topGeoOpp ? geoName(topGeoOpp.segment) : '—'}
            sub={topGeoOpp ? `${fmtNum(topGeoOpp.opportunity_users)} users · ${topGeoOpp.opportunity_pct}% gap` : ''}
          />
        </div>
      )}

      {/* Age Band opportunity */}
      <div className="trends-section-label">
        Opportunity by Age Band
        {oppUsersAge > 0 && <span style={{ fontWeight: 400, marginLeft: 8, color: COLORS.textMuted }}>— {fmtNum(oppUsersAge)} total</span>}
      </div>
      {ageReq.loading && <ChartLoader />}
      {ageReq.error   && <ChartError message={ageReq.error} />}
      {!ageReq.loading && !ageReq.error && ageRows.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
          <ChartCard title="Age Band — Opportunity Users" sub={`Category users not yet on ${selApp}`}>
            <BarChart
              categories={ageRows.map(r => r.segment)}
              series={oppSeries(ageRows)}
              height={280}
              xFormatter={v => fmtNum(v)}
            />
          </ChartCard>
          <ChartCard title="Age Band — Opportunity Gap %" sub="Penetration headroom per age group">
            <BarChart
              categories={ageRows.map(r => r.segment)}
              series={oppPctSeries(ageRows)}
              height={280}
              xFormatter={v => `${v}%`}
            />
          </ChartCard>
        </div>
      )}

      {/* Geography opportunity — map */}
      <div className="trends-section-label">
        Opportunity by Geography
        {oppUsersGeo > 0 && <span style={{ fontWeight: 400, marginLeft: 8, color: COLORS.textMuted }}>— {fmtNum(oppUsersGeo)} total</span>}
      </div>
      <div style={{ marginBottom: 16 }}>
        <OpportunityGeoMap
          geoRows={geoReq.data || []}
          selApp={selApp}
          effectiveCat={effectiveCat}
          selectedMonth={selectedMonth}
        />
      </div>

      {/* Gender opportunity */}
      <div className="trends-section-label">
        Opportunity by Gender
        {oppUsersGender > 0 && <span style={{ fontWeight: 400, marginLeft: 8, color: COLORS.textMuted }}>— {fmtNum(oppUsersGender)} total</span>}
      </div>
      {genderReq.loading && <ChartLoader height={160} />}
      {genderReq.error   && <ChartError message={genderReq.error} height={160} />}
      {!genderReq.loading && !genderReq.error && genderRows.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
          <ChartCard title="Gender — Opportunity Users" sub={`Category users not yet on ${selApp}`}>
            <BarChart
              categories={genderRows.map(r => r.segment)}
              series={oppSeries(genderRows)}
              height={200}
              xFormatter={v => fmtNum(v)}
            />
          </ChartCard>
          <ChartCard title="Gender — Opportunity Gap %" sub="Penetration headroom per gender">
            <BarChart
              categories={genderRows.map(r => r.segment)}
              series={oppPctSeries(genderRows)}
              height={200}
              xFormatter={v => `${v}%`}
            />
          </ChartCard>
        </div>
      )}
    </div>
  );
}

// ── View 3: Penetration Targets ───────────────────────────────────────────────

export function PenetrationTargetsView() {
  const state = useGrowthState();
  const { meta, effectiveCat, selectedMonth, sessionType, setSessionType, apps, selApp, setApp, enabled, params } = state;

  const ageReq    = useApi(Growth.opportunitiesByAge,    params, enabled);
  const genderReq = useApi(Growth.opportunitiesByGender, params, enabled);
  const geoReq    = useApi(Growth.opportunitiesByGeo,    { ...params, limit: 30 }, enabled);

  const ageRows    = useMemo(() => sortByAge(ageReq.data    || []), [ageReq.data]);
  const genderRows = useMemo(() => (genderReq.data || []), [genderReq.data]);
  const geoRows    = useMemo(() => (geoReq.data   || []), [geoReq.data]);

  // Combined flat list with dim tag, for bubble chart + table
  const allRows = useMemo(() => [
    ...ageRows.map(r    => ({ ...r, dim: 'age',    displaySegment: r.segment })),
    ...genderRows.map(r => ({ ...r, dim: 'gender', displaySegment: GENDER_LABELS[r.segment] || r.segment })),
    ...geoRows.map(r    => ({ ...r, dim: 'geo',    displaySegment: geoName(r.segment) })),
  ], [ageRows, genderRows, geoRows]);

  const ranked = useMemo(
    () => [...allRows].sort((a, b) => b.opportunity_users - a.opportunity_users),
    [allRows],
  );

  const bubbleData = useMemo(
    () => allRows
      .filter(r => r.opportunity_users > 0)
      .map(r => ({
        x:     r.universe,
        y:     r.opportunity_pct,
        size:  r.opportunity_users,
        label: r.displaySegment,
        dim:   r.dim,
      })),
    [allRows],
  );

  const oppAge    = sumField(ageRows,    'opportunity_users');
  const oppGender = sumField(genderRows, 'opportunity_users');
  const oppGeo    = sumField(geoRows,    'opportunity_users');

  const nat = ageRows[0] || null;

  const loading = ageReq.loading || genderReq.loading || geoReq.loading;
  const error   = ageReq.error   || genderReq.error   || geoReq.error;

  return (
    <div>
      <FilterBar meta={meta} sessionType={sessionType} setSessionType={setSessionType} apps={apps} selApp={selApp} setApp={setApp} />

      <div className="kpi-grid" style={{ marginBottom: 16 }}>
        <KpiTile
          label="Opportunity by Age (sum)"
          value={fmtNum(oppAge)}
          sub="Category users not on brand — age dim"
        />
        <KpiTile
          label="Opportunity by Gender (sum)"
          value={fmtNum(oppGender)}
          sub="Category users not on brand — gender dim"
        />
        <KpiTile
          label="Opportunity by Geo — Top 30 (sum)"
          value={fmtNum(oppGeo)}
          sub="Category users not on brand — top 30 areas"
        />
        {nat && (
          <KpiTile
            label={`${selApp} National Penetration`}
            value={`${nat.national_brand_pct}%`}
            sub={`vs ${nat.national_cat_pct}% category · ${fmtMonth(selectedMonth)}`}
          />
        )}
      </div>

      {/* Priority matrix */}
      {loading && <ChartLoader height={420} />}
      {error   && <ChartError message={error}  height={420} />}
      {!loading && !error && bubbleData.length > 0 && (
        <ChartCard
          title="Priority Matrix — Segment Universe vs Opportunity Gap"
          sub={`${effectiveCat} · ${selApp} · ${fmtMonth(selectedMonth)} · bubble size = opportunity users · quadrant lines at median values`}
          style={{ marginBottom: 16 }}
        >
          <BubbleChart
            data={bubbleData}
            height={460}
            xLabel="Segment Universe"
            yLabel="Opportunity Gap %"
          />
        </ChartCard>
      )}

      {/* Ranked table */}
      {!loading && !error && ranked.length > 0 && (
        <div className="card">
          <div className="card-header">
            <div>
              <div className="card-title">All Segments — Ranked by Opportunity</div>
              <div className="card-sub">
                {ranked.length} segments · sorted by opportunity users descending
              </div>
            </div>
          </div>
          <div style={{ overflowX: 'auto', maxHeight: 520, overflowY: 'auto' }}>
            <table className="data-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Dimension</th>
                  <th>Segment</th>
                  <th style={{ textAlign: 'right' }}>Universe</th>
                  <th style={{ textAlign: 'right' }}>Cat Users</th>
                  <th style={{ textAlign: 'right' }}>Brand Users</th>
                  <th style={{ textAlign: 'right' }}>Opportunity</th>
                  <th style={{ textAlign: 'right' }}>Gap %</th>
                </tr>
              </thead>
              <tbody>
                {ranked.map((r, i) => (
                  <tr key={`${r.dim}-${r.segment}`} className={i % 2 ? 'alt' : ''}>
                    <td style={{ color: COLORS.textMuted }}>{i + 1}</td>
                    <td>
                      <span style={{
                        display: 'inline-block',
                        padding: '1px 8px',
                        borderRadius: 10,
                        fontSize: 11,
                        fontWeight: 600,
                        background: DIM_COLORS[r.dim] + '22',
                        color: DIM_COLORS[r.dim],
                      }}>
                        {DIM_LABELS[r.dim]}
                      </span>
                    </td>
                    <td><strong>{r.displaySegment}</strong></td>
                    <td style={{ textAlign: 'right' }}>{fmtNum(r.universe)}</td>
                    <td style={{ textAlign: 'right' }}>{fmtNum(r.cat_users)}</td>
                    <td style={{ textAlign: 'right' }}>{fmtNum(r.brand_users)}</td>
                    <td style={{ textAlign: 'right', color: COLORS.teal, fontWeight: 600 }}>{fmtNum(r.opportunity_users)}</td>
                    <td style={{ textAlign: 'right', color: r.opportunity_pct > 0 ? COLORS.positive : COLORS.textMuted }}>
                      {r.opportunity_pct}%
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
